import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import isUndefined from 'lodash/isUndefined';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {window} from 'ssr-window';
import APIUtils from '@spothero/utils/api';
import DOMUtils from '@spothero/utils/dom';
import EnvironmentUtils from '@spothero/utils/environment';
import WithConfig from 'config/WithConfig';
import SegmentUtils from 'utils/segment';
import {GOOGLE_MAPS_LAT_LNG} from '../search-prop-types';
import NoResultsModal from './NoResultsModal';
import SpotMapControls from './SpotMapControls';
import SHGoogleMap from './SHGoogleMap';

class SpotMap extends Component {
    static propTypes = {
        searchRequestId: PropTypes.string,
        mapOptions: PropTypes.object,
        isMobile: PropTypes.bool,
        isFiltered: PropTypes.bool,
        zoom: PropTypes.number,
        onZoom: PropTypes.func,
        onMapMoved: PropTypes.func,
        idealDistance: PropTypes.number,
        overrideIdealZoom: PropTypes.bool,
        destination: PropTypes.shape({
            title: PropTypes.string.isRequired,
            latitude: PropTypes.number.isRequired,
            longitude: PropTypes.number.isRequired,
            position: GOOGLE_MAPS_LAT_LNG
        }).isRequired,
        spots: PropTypes.arrayOf(PropTypes.object).isRequired,
        isMonthly: PropTypes.bool,
        starts: PropTypes.string,
        ends: PropTypes.string,
        setSearchMap: PropTypes.func,
        mapLoadingIncrement: PropTypes.number,
        requestIncrements: PropTypes.number,
        onMarkerClick: PropTypes.func.isRequired,
        loadRadialSpots: PropTypes.func.isRequired,
        loadTiledSpots: PropTypes.func.isRequired,
        toggleFiltersClick: PropTypes.func,
        resetFiltersClick: PropTypes.func,
        findParkingNearMe: PropTypes.func,
        getPrice: PropTypes.func,
        kmlMapLayerURL: PropTypes.string,
        searchType: PropTypes.string,
        kmlMapLayerEnabled: PropTypes.bool,
        setShimOverMap: PropTypes.bool,
        onShimClick: PropTypes.func,
        selectedSpot: PropTypes.object,
        staticBaseUrl: PropTypes.string.isRequired,
        isPrerender: PropTypes.bool,
        geolocated: PropTypes.bool
    };

    constructor(props) {
        super(props);

        const isBrowser = EnvironmentUtils.isBrowser();
        const {
            searchRequestId,
            spots,
            isMonthly,
            zoom,
            mapOptions
        } = props;

        this._gmaps = (isBrowser) ? window.google.maps : null;
        this._initialZoom = zoom;
        this._isMapLoaded = false;
        this._isInitialSearchComplete = false;
        this._isIdealDistanceSet = false;
        this._isAutoZooming = false;
        this._isReturningToDestination = false;
        this._isDragging = false;
        this._infoWindowsDisabled = false;
        this._noResultsRetryCount = 0;
        this._mapOptions = {
            disableDefaultUI: true,
            zoomControl: false,
            scrollwheel: false,
            draggable: true,
            styles: [{
                featureType: 'poi',
                stylers: [{
                    visibility: 'off'
                }]
            }, {
                featureType: 'transit.station.rail',
                elementType: 'labels.icon',
                stylers: [{
                    saturation: -100
                }]
            }, {
                featureType: 'transit.station.bus',
                elementType: 'labels.icon',
                stylers: [{
                    saturation: -100
                }]
            }],
            mapTypeId: (isBrowser) ? this._gmaps.MapTypeId.ROADMAP : null,
            ...mapOptions
        };

        this.state = {
            showNoResultsModal: false,
            isDestinationOutOfBounds: false,
            markers: [],
            prevSearchRequestId: searchRequestId,
            prevSpots: spots,
            prevIsMonthly: isMonthly
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const {
            searchRequestId,
            spots,
            isMonthly
        } = nextProps;
        const {
            prevSearchRequestId,
            prevSpots,
            prevIsMonthly
        } = prevState;
        const newState = {};

        if (isMonthly !== prevIsMonthly) {
            newState.showNoResultsModal = false;
            newState.prevIsMonthly = isMonthly;
        }

        if (searchRequestId !== prevSearchRequestId || spots.length !== prevSpots.length) {
            newState.prevSearchRequestId = searchRequestId;
            newState.prevSpots = spots;

            // ensure spots with either missing selectedRate
            // or those without the proper selectedRate.rule_type are not offered to users
            newState.markers = spots
                .filter(({selectedRate}) => selectedRate && (
                    (isMonthly && selectedRate.rule_type === 'monthly')
                    || (!isMonthly && selectedRate.rule_type !== 'monthly')
                ))
                .map(spot => ({
                    spotId: spot.spotId,
                    spot,
                    state: 'default'
                }));
        }

        return (!isEmpty(newState)) ? newState : null;
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            searchRequestId,
            spots,
            isMonthly,
            starts,
            ends,
            isFiltered,
            zoom,
            onZoom,
            kmlMapLayerEnabled,
            geolocated
        } = this.props;
        const {
            searchRequestId: prevSearchRequestId,
            zoom: prevZoom,
            geolocated: prevGeolocated
        } = prevProps;

        if (prevSearchRequestId !== searchRequestId && this.googleMap) {
            this._initialZoom = zoom;
            this._setIdealDistance(true);
            this._onNewSearch({
                reset: true
            });

            if (spots.length && !kmlMapLayerEnabled && !this._infoWindowsDisabled) {
                this._disableInfoWindows();
            }
        } else if (prevProps.isMonthly !== isMonthly) {
            this._noResultsRetryCount = 0;
            // we don't reset the zoom on monthly toggle as the zoom update will be handled within
            // the search reducer SEARCH_REQUEST_SET_TERM case
            this._onNewSearch({
                autoZoom: true,
                resetZoom: false
            });
        } else {
            if ((isMonthly && prevProps.starts !== starts)
                || (!isMonthly && (prevProps.starts !== starts || prevProps.ends !== ends))) {
                this._onNewSearch({
                    dateTimeChanged: true
                });
            }
        }

        if (prevProps.isFiltered !== isFiltered && !spots.length) {
            // typically if you're filtered and search a new area which has no spots we need to make sure
            // that we show the no results modal on top of the map
            this._onNoResults();
        }

        if (this._isMapLoaded && !this._isIdealDistanceSet) {
            this._isIdealDistanceSet = true;
            this._initialZoom = zoom;
        }

        if (zoom !== prevZoom) {
            if (!this._isReturningToDestination) {
                if (zoom < prevZoom) {
                    this._onDragEnd(true);
                }
            }

            this._isReturningToDestination = false;
        }

        if (!prevGeolocated && geolocated) {
            this._onNewSearch({
                reset: true
            });
            onZoom(15);
        }
    }

    componentWillUnmount() {
        this._cancelSpotsLoader();

        if (this._isDraggingTimeout) {
            clearTimeout(this._isDraggingTimeout);
        }
    }

    _onGoogleMapLoaded = mapComponent => {
        const {
            spots,
            geolocated,
            onZoom
        } = this.props;

        if (mapComponent) {
            const {
                setSearchMap
            } = this.props;

            this.googleMap = mapComponent;

            if (setSearchMap) {
                setSearchMap(this.googleMap);
            }

            if (!this._isMapLoaded && !this.props.isPrerender) {
                this._setIdealDistance();
            }
        }

        if (spots.length && geolocated) {
            this._onNewSearch({
                reset: true
            });
            onZoom(15);
        }
    }

    _onNewSearch = ({reset = false, dateTimeChanged = false, autoZoom = false, resetZoom = true}) => {
        if (!this.googleMap) {
            return;
        }

        this._cancelSpotsLoader();

        if (reset) {
            this._onReturnToDestinationClick({
                callback: this._loadRadialSpots,
                dateTimeChanged,
                autoZoom,
                resetZoom
            });
        } else {
            this._loadRadialSpots({dateTimeChanged, autoZoom});
        }
    }

    _onZoomIn = () => {
        const {
            zoom,
            onZoom
        } = this.props;

        if (zoom > 19) { return; }

        onZoom(zoom + 1);
    }

    _onZoomOut = () => {
        const {
            zoom,
            onZoom
        } = this.props;

        if (zoom < 4) { return; }

        onZoom(zoom - 1);
    }

    _onDragStart = () => {
        this._isDragging = true;

        if (this._isDraggingTimeout) {
            clearTimeout(this._isDraggingTimeout);
        }
    }

    _onDragEnd = zoomed => {
        if (!this._isInitialSearchComplete) {
            return;
        }

        this._isDraggingTimeout = setTimeout(() => {
            this._isDragging = false;
            this._onMapMoved(zoomed);
        }, 500);
    }

    _onMapMoved = zoomed => {
        const {
            onMapMoved,
            destination,
            loadTiledSpots
        } = this.props;
        const mapBounds = this.googleMap.getBounds();

        this._cancelSpotsLoader();

        if (onMapMoved) {
            onMapMoved({
                zoomed,
                autoZoomed: this._isAutoZooming
            });
        }

        this._spotsLoader = APIUtils.makeCancelable(
            loadTiledSpots(mapBounds)
        );
        this._spotsLoader
            .promise
            .then(() => {
                this._checkLoadedSpots();
            })
            .catch(error => {
                if (!has(error, 'isCanceled')) {
                    SegmentUtils.track({
                        event: 'Error Message Displayed',
                        properties: {
                            'error message': 'Error fetching spots',
                            'error screen': 'search',
                            'error action': 'fetching spots'
                        }
                    });
                }
            });

        // is the destination marker outside of the new map bounds?
        this.setState({
            isDestinationOutOfBounds: !mapBounds.contains(destination.position)
        });
    }

    _onMarkerClick = spotId => {
        if (this._isDragging) { return; }

        this.props.onMarkerClick(spotId);
    }

    _onReturnToDestinationClick = ({callback, dateTimeChanged, autoZoom, resetZoom}) => {
        const {
            destination,
            onZoom,
            setSearchMap
        } = this.props;

        if (this.googleMap) {
            this._isReturningToDestination = true;
            this.googleMap.panTo(destination.position);
            this.setState({
                isDestinationOutOfBounds: false
            });

            if (resetZoom) {
                onZoom(this._initialZoom);
            }

            setSearchMap(this.googleMap);

            // there is no way to know when a panTo finishes except that the map goes into idle mode
            this._gmaps.event.addListenerOnce(this.googleMap, 'idle', () => {
                if (callback) {
                    callback({dateTimeChanged, autoZoom});
                }
            });
        }
    }

    _onNoResults = () => {
        DOMUtils.remove('.Tooltip:not(.Tooltip-no-close), .tooltip:not(.tooltip-no-close)');

        this.setState({
            showNoResultsModal: true
        });
    }

    _onResultsFound = () => {
        this.setState({
            showNoResultsModal: false
        });
    }

    _onNoResultsModalFilterResultsClick = () => {
        const {
            toggleFiltersClick
        } = this.props;

        this.setState({
            showNoResultsModal: false
        });

        if (toggleFiltersClick) {
            toggleFiltersClick();
        }
    }

    _onNoResultsModalResetFiltersClick = () => {
        const {
            resetFiltersClick
        } = this.props;

        this.setState({
            showNoResultsModal: false
        });

        if (resetFiltersClick) {
            resetFiltersClick();
        }
    }

    _onSpotMapShimClick = evt => {
        const {
            onShimClick
        } = this.props;

        if (isFunction(onShimClick)) {
            onShimClick(evt);
        }
    }

    _loadRadialSpots = ({dateTimeChanged, autoZoom}) => {
        this._spotsLoader = APIUtils.makeCancelable(
            this.props.loadRadialSpots({
                dateTimeChanged,
                autoZoom
            })
        );
        this._spotsLoader
            .promise
            .then(() => {
                this._checkLoadedSpots();
            })
            .catch(error => {
                if (!has(error, 'isCanceled')) {
                    SegmentUtils.track({
                        event: 'Error Message Displayed',
                        properties: {
                            'error message': 'Error fetching spots',
                            'error screen': 'search',
                            'error action': 'fetching spots'
                        }
                    });
                }
            });

        this._isInitialSearchComplete = true;
    }

    _cancelSpotsLoader() {
        if (this._spotsLoader) {
            this._spotsLoader.cancel();
            this._spotsLoader = null;
        }
    }

    _disableInfoWindows() {
        // Here we redefine set() method.
        // If it is called for map option, we hide InfoWindow, if "noSupress" option isnt true.
        // As Google doesn't know about this option, its InfoWindows will not be opened.
        const set = this._gmaps.InfoWindow.prototype.set;

        this._gmaps.InfoWindow.prototype.set = (key, val) => {
            if (key === 'map' && !this.get('noSupress')) { return; }

            set.apply(this, arguments); // eslint-disable-line prefer-rest-params
        };

        this._infoWindowsDisabled = true;
    }

    _setIdealDistance(preventNewSearch) {
        const {
            idealDistance,
            overrideIdealZoom,
            destination,
            onZoom,
            searchType
        } = this.props;

        this._isMapLoaded = true;

        if (idealDistance && !overrideIdealZoom && (!searchType || searchType !== 'address')) {
            const circle = new this._gmaps.Circle({
                radius: idealDistance,
                center: destination.position
            });

            this._isAutoZooming = true;

            if (this.googleMap) {
                this.googleMap.fitBounds(circle.getBounds());
                onZoom(this.googleMap.getZoom());
            }
        } else {
            this._isAutoZooming = false;
            this._isIdealDistanceSet = true;
        }

        if (!window.location.pathname.match(/parking-near-me/) && !preventNewSearch) {
            this._onNewSearch({
                reset: false
            });
        }
    }

    _checkLoadedSpots() {
        const {
            spots,
            zoom,
            onZoom
        } = this.props;
        let newZoom = zoom;

        // if no results are found
        if (!spots.length) {
            // first time no results are found for each search let's try to zoom out once and search again
            if (this._noResultsRetryCount < 2 && zoom > 12) {
                newZoom = zoom - 1;
                this._isAutoZooming = true;

                this._noResultsRetryCount++;
                onZoom(newZoom);
            } else {
                this._isAutoZooming = false;
                this._noResultsRetryCount = 0;
                this._onNoResults();
            }
        } else {
            this._isAutoZooming = false;
            this._noResultsRetryCount = 0;
            this._onResultsFound();
        }
    }

    triggerResize() {
        this._gmaps.event.trigger(this.googleMap, 'resize');
    }

    render() {
        const {
            searchRequestId,
            isMobile,
            isFiltered,
            destination,
            mapLoadingIncrement,
            requestIncrements,
            kmlMapLayerURL,
            kmlMapLayerEnabled,
            selectedSpot,
            zoom,
            setShimOverMap,
            staticBaseUrl,
            findParkingNearMe,
            getPrice,
        } = this.props;
        const {
            isDestinationOutOfBounds,
            showNoResultsModal,
            markers
        } = this.state;
        const {
            title: destinationTitle,
            position
        } = destination;
        const classes = classNames(
            'SpotMap',
            {'SpotMap-hidden': !this._isMapLoaded}
        );

        return (
            <div className={classes}>
                {setShimOverMap &&
                    <div
                        className="SpotMap-shim"
                        onClick={this._onSpotMapShimClick}
                    />
                }
                <div className="SpotMap-map">
                    <SHGoogleMap
                        searchRequestId={searchRequestId}
                        markers={markers}
                        zoom={zoom}
                        position={position}
                        destination={destination}
                        destinationTitle={destinationTitle}
                        kmlMapLayerEnabled={kmlMapLayerEnabled}
                        kmlMapLayerURL={kmlMapLayerURL}
                        mapOptions={this._mapOptions}
                        onDragStart={this._onDragStart}
                        onDragEnd={this._onDragEnd}
                        onMarkerClick={this._onMarkerClick}
                        onGoogleMapLoaded={this._onGoogleMapLoaded}
                        getPrice={getPrice}
                        selectedSpot={selectedSpot}
                        staticBaseUrl={staticBaseUrl}
                    />
                    {!isMobile && showNoResultsModal &&
                        <NoResultsModal
                            isFiltered={isFiltered}
                            onHidden={this._onResultsFound}
                            onFilterResults={this._onNoResultsModalFilterResultsClick}
                            onResetFilters={this._onNoResultsModalResetFiltersClick}
                        />
                    }
                </div>
                <SpotMapControls
                    isDestinationOutOfBounds={isDestinationOutOfBounds}
                    isFiltered={isFiltered}
                    zoom={zoom}
                    destinationTitle={destinationTitle}
                    onZoomIn={this._onZoomIn}
                    onZoomOut={this._onZoomOut}
                    onGoToCurrentLocation={findParkingNearMe}
                    onReturnToDestination={this._onReturnToDestinationClick}
                />
                {(mapLoadingIncrement > 0 && !isUndefined(requestIncrements)) &&
                    <div
                        className="SpotMap-loader"
                        style={{width: `${mapLoadingIncrement * (100 / requestIncrements)}%`}}
                    />
                }
            </div>
        );
    }
}

export default WithConfig(SpotMap);
